const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]';

function headingLevel(element: Element): number | null {
    const role = element.getAttribute('role');
    if (role !== null && role.trim().toLowerCase() !== 'heading') {
        return null;
    }
    const ariaLevel = Number.parseInt(element.getAttribute('aria-level') ?? '', 10);
    if (Number.isInteger(ariaLevel) && ariaLevel >= 1) {
        return ariaLevel;
    }
    const match = /^H([1-6])$/.exec(element.tagName);
    return match ? Number(match[1]) : null;
}

/**
 * Repairs skipped heading levels in third-party HTML (e.g. an h4 directly under an h2) so the
 * outline screen reader users navigate is logical. Each heading is capped at one level below its
 * nearest preceding higher-level heading; `parentLevel` is the heading level of the page that
 * hosts the content, so a document starting at h3 under an h1 page title is lifted to level 2.
 *
 * Levels are corrected with `aria-level` rather than by renaming elements, so the document's own
 * styles (which often target `h4` etc.) are unaffected. HTML that needs no change is returned as-is.
 */
export function normalizeHeadingLevels(html: string, parentLevel = 1): string {
    // DOMParser output is inert: scripts don't run and resources don't load.
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const ancestors: Array<{ original: number; effective: number }> = [];
    let changed = false;

    doc.querySelectorAll(HEADING_SELECTOR).forEach((heading) => {
        const original = headingLevel(heading);
        if (original === null) {
            return;
        }
        while (ancestors.length > 0 && ancestors[ancestors.length - 1].original >= original) {
            ancestors.pop();
        }
        const parentEffective = ancestors.length > 0 ? ancestors[ancestors.length - 1].effective : parentLevel;
        // A level-1 heading is never demoted; it simply starts a new top-level section.
        const effective = original === 1 ? 1 : Math.min(original, parentEffective + 1);
        ancestors.push({ original, effective });

        if (effective !== original) {
            heading.setAttribute('aria-level', String(effective));
            changed = true;
        }
    });

    if (!changed) {
        return html;
    }
    const doctype = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>` : '';
    return `${doctype}${doc.documentElement.outerHTML}`;
}
